import React, { useState, useCallback, useRef } from 'react';
import classnames from 'classnames/bind';
import { useIntl } from 'umi';
import { Popover } from 'antd';
import { Tooltip, Icon } from '@/components';
import * as QtBridge from '@/utils/Bridge';
import Style from './index.less';
import { Tool } from './Tool';
import { brushTool, BrushTool } from './BrushTool';
import { eraserTool } from './EraserTool';
import { laserTool } from './LaserTool';
import { selectTool } from './SelectTool';
import { shapeTool, ShapeTool } from './ShapeTool';
import { textTool } from './TextTool';
import ColorPalette from './ColorPalette';

export { brushTool, BrushTool } from './BrushTool';
export { eraserTool } from './EraserTool';
export { laserTool } from './LaserTool';
export { selectTool } from './SelectTool';
export { shapeTool, ShapeTool } from './ShapeTool';
export { textTool } from './TextTool';

const $c = classnames.bind(Style);

const Toolbar: React.FC<{ tool: Tool | undefined; onChange: (nextTool: Tool) => void }> = ({
  tool,
  onChange: propsOnChange,
}) => {
  const { formatMessage } = useIntl();

  const containerRef = useRef<HTMLDivElement>(null);

  const lastShapeToolIcon = ShapeTool.SHAPE_TYPE_ICON[shapeTool.type];

  const [, reactUpdate] = useState(0);
  const forceUpdate = useCallback(() => reactUpdate((pre) => pre + 1), []);

  const onChange = useCallback(
    (nextTool: Tool) => {
      propsOnChange(nextTool);
      forceUpdate();
    },
    [propsOnChange, forceUpdate]
  );

  const onBrushColorChange = useCallback<Parameters<typeof ColorPalette>[0]['onChange']>(
    (e) => {
      brushTool.setAttributes({ color: e.color });
      onChange(brushTool);
    },
    [onChange]
  );

  const onShapeColorChange = useCallback<Parameters<typeof ColorPalette>[0]['onChange']>(
    (e) => {
      shapeTool.setAttributes({ color: e.color });
      onChange(shapeTool);
    },
    [onChange]
  );

  return (
    <div className={$c('toolbar')} ref={containerRef}>
      {[selectTool, brushTool, shapeTool, textTool, eraserTool, laserTool].map((item) => {
        QtBridge.buriedPoint({
          key: 'whiteboard_toolbar',
          value: new Map<Tool, string>([
            [selectTool, 'wb_toolbar_mouse'],
            [brushTool, 'wb_toolbar_draw'],
            [shapeTool, 'wb_toolbar_square'],
            [textTool, 'wb_toolbar_word'],
            [eraserTool, 'wb_toolbar_erase'],
            [laserTool, 'wb_toolbar_highlight'],
          ]).get(item)!,
        });

        if (item === brushTool) {
          return (
            <Popover
              key={item.title}
              overlayClassName="FSknZpQqEJ"
              title={null}
              visible={tool === brushTool}
              placement="right"
              align={{ offset: [-6, 0] }}
              getPopupContainer={() => containerRef.current!}
              content={
                <>
                  <ColorPalette color={brushTool.color} onChange={onBrushColorChange} />
                  <div className="brush-type">
                    {[BrushTool.BRUSH_TYPE.BRUSH, BrushTool.BRUSH_TYPE.MARK].map((brushType) => {
                      return (
                        <div
                          key={brushType}
                          className={classnames('click', {
                            selected: brushTool.type === brushType,
                          })}
                          onClick={() => {
                            brushTool.setAttributes({ type: brushType });
                            onChange(brushTool);
                          }}
                        >
                          <Icon name={BrushTool.BRUSH_TYPE_ICON[brushType]} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="brush-width">
                    {[BrushTool.BRUSH_WIDTH.THIN, BrushTool.BRUSH_WIDTH.THICK].map((brushWidth) => {
                      return (
                        <div
                          key={brushWidth}
                          className={classnames(brushWidth, {
                            selected: brushTool.width === brushWidth,
                          })}
                          onClick={() => {
                            brushTool.setAttributes({ width: brushWidth });
                            onChange(brushTool);
                          }}
                        />
                      );
                    })}
                  </div>
                </>
              }
            >
              <Tooltip
                key={item.title}
                className="VahO1nUmji"
                placement="right"
                align={{ offset: [-4, 0] }}
                title={
                  <span className={$c('toolbar-left-icon-tooltip')}>
                    {formatMessage({ id: item.title })}
                  </span>
                }
              >
                <div
                  className={$c('click', { selected: tool === brushTool })}
                  onClick={() => {
                    onChange(brushTool);
                  }}
                >
                  <Icon name={brushTool.icon} className={$c('icon')} />
                </div>
              </Tooltip>
            </Popover>
          );
        }
        if (item === shapeTool) {
          return (
            <Popover
              key={item.title}
              overlayClassName="FSknZpQqEJ"
              title={null}
              visible={tool === shapeTool}
              placement="right"
              align={{ offset: [-6, 0] }}
              getPopupContainer={() => containerRef.current!}
              content={
                <>
                  <ColorPalette color={shapeTool.color} onChange={onShapeColorChange} />
                  <div className="shape-selection">
                    {[
                      ShapeTool.SHAPE_TYPE.RECT_FILL,
                      ShapeTool.SHAPE_TYPE.ELLIPSE_FILL,
                      ShapeTool.SHAPE_TYPE.RECT,
                      ShapeTool.SHAPE_TYPE.ELLIPSE,
                      ShapeTool.SHAPE_TYPE.LINE_ARROW,
                      ShapeTool.SHAPE_TYPE.LINE,
                    ].map((shapeType) => {
                      const icon = ShapeTool.SHAPE_TYPE_ICON[shapeType]!;
                      return (
                        <div
                          key={shapeType}
                          className={classnames('click', {
                            selected: shapeTool.type === shapeType,
                          })}
                          onClick={() => {
                            shapeTool.setAttributes({ type: shapeType });
                            onChange(shapeTool);
                          }}
                        >
                          {typeof icon === 'string' ? (
                            <Icon className={classnames('shape-icon')} name={icon} />
                          ) : (
                            <div className={classnames('shape-icon')}>
                              <Icon className="highlight" mode="symbol" name={icon.highlight} />
                              <Icon className="normal" mode="symbol" name={icon.normal} />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              }
            >
              <Tooltip
                key={item.title}
                className="VahO1nUmji"
                placement="right"
                align={{ offset: [-4, 0] }}
                title={
                  <span className={$c('toolbar-left-icon-tooltip')}>
                    {formatMessage({ id: item.title })}
                  </span>
                }
              >
                <div
                  className={$c('click', { selected: tool === shapeTool })}
                  onClick={() => {
                    onChange(shapeTool);
                  }}
                >
                  {typeof lastShapeToolIcon === 'string' ? (
                    <Icon name={lastShapeToolIcon} className={$c('icon')} />
                  ) : (
                    <div className={$c('icon')}>
                      <Icon
                        className={$c('highlight')}
                        mode="symbol"
                        name={lastShapeToolIcon.highlight}
                      />
                      <Icon
                        className={$c('normal')}
                        mode="symbol"
                        name={lastShapeToolIcon.normal}
                      />
                    </div>
                  )}
                </div>
              </Tooltip>
            </Popover>
          );
        }
        return (
          <Tooltip
            key={item.title}
            className="VahO1nUmji"
            placement="right"
            align={{ offset: [-4, 0] }}
            title={
              <span className={$c('toolbar-left-icon-tooltip')}>
                {formatMessage({ id: item.title })}
              </span>
            }
          >
            {item !== brushTool && item !== shapeTool && (
              <div
                className={$c('click', { selected: tool === item })}
                onClick={() => {
                  onChange(item);
                }}
              >
                <Icon name={item.icon!} className={$c('icon')} />
              </div>
            )}
          </Tooltip>
        );
      })}
    </div>
  );
};

export default Toolbar;
